import { useLayoutEffect, useRef, type RefObject } from 'react';

/** FLIP (First-Last-Invert-Play) for a container whose descendants carry `data-flip-key`. */
export function useFlip<T extends HTMLElement>(duration = 220): RefObject<T> {
  const ref = useRef<T>(null);
  const rectsRef = useRef<Map<string, DOMRect>>(new Map());

  // No dep array on purpose (see docs/specs/REV29-motion-patterns.md §2): this needs to run after
  // every render, not just mount, so a row that moves between the pending and done lists (matched
  // by `data-flip-key`, not DOM node identity) is measured before *and* after each update. It's
  // cheap — one `querySelectorAll` plus N `getBoundingClientRect` calls.
  useLayoutEffect(() => {
    const container = ref.current;
    if (!container) {
      return;
    }

    const previousRects = rectsRef.current;
    const nextRects = new Map<string, DOMRect>();
    const elements = container.querySelectorAll<HTMLElement>('[data-flip-key]');
    // Positions are stored relative to the container, not the viewport: a re-render that happens
    // after the user scrolled (e.g. a TanStack refetch on window focus) must not read the scroll
    // delta as movement and slide every row at once.
    const origin = container.getBoundingClientRect();

    // Computed once per effect run, not per element.
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    elements.forEach((el) => {
      const key = el.dataset.flipKey;
      if (key === undefined) {
        return;
      }

      const viewportRect = el.getBoundingClientRect();
      const rect = new DOMRect(
        viewportRect.left - origin.left,
        viewportRect.top - origin.top,
        viewportRect.width,
        viewportRect.height,
      );
      nextRects.set(key, rect);

      const previousRect = previousRects.get(key);
      if (!previousRect) {
        return;
      }

      const dx = previousRect.left - rect.left;
      const dy = previousRect.top - rect.top;
      if (dx === 0 && dy === 0) {
        return;
      }

      // jsdom (unit tests) has no `Element.prototype.animate`; reduced-motion skips the play step
      // too — both still record rects above so a later run has a correct baseline.
      if (typeof el.animate !== 'function' || reducedMotion) {
        return;
      }

      el.animate(
        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
        { duration, easing: 'ease-out' },
      );
    });

    rectsRef.current = nextRects;
  });

  return ref;
}
